<template>
  <div class="submissions-page">
    <div class="page-container">
      <div class="header-card">
        <h1 class="page-title">Бүртгэлийн мэдээлэл</h1>
        <div class="stats-row">
          <div class="stat-card">
            <div class="stat-number">{{ submissions.length }}</div>
            <div class="stat-label">Нийт бүртгэл</div>
          </div>
        </div>
      </div>

      <div class="content-area">
        <div v-if="loading" class="loading-state">
          <div class="loader"></div>
          <p>Уншиж байна...</p>
        </div>

        <div v-else-if="submissions.length === 0" class="empty-state">
          <div class="empty-icon">📝</div>
          <h2>Бүртгэл байхгүй байна</h2>
          <p>Одоогоор ямар ч бүртгэл ирээгүй байна.</p>
        </div>

        <div v-else>
          <div class="submissions-grid">
            <div 
              v-for="(submission, index) in submissions" 
              :key="submission.id"
              class="submission-card"
            >
              <div class="card-header">
                <div class="submission-number">#{{ submissions.length - index }}</div>
                <div class="submission-date">
                  {{ formatDate(submission.createdAt) }}
                </div>
              </div>

              <div class="card-content">
                <div class="info-grid">
                  <div class="info-item" v-if="submission.fullName">
                    <div class="info-label">Овог нэр</div>
                    <div class="info-value">{{ submission.fullName }}</div>
                  </div>
                  
                  <div class="info-item" v-if="submission.organization">
                    <div class="info-label">Байгууллагын нэр</div>
                    <div class="info-value">{{ submission.organization }}</div>
                  </div>
                  
                  <div class="info-item" v-if="submission.email">
                    <div class="info-label">Холбоо барих имэйл</div>
                    <div class="info-value">{{ submission.email }}</div>
                  </div>
                  
                  <div class="info-item" v-if="submission.phone">
                    <div class="info-label">Холбоо барих утас</div>
                    <div class="info-value">{{ submission.phone }}</div>
                  </div>
                  
                  <div class="info-item" v-if="submission.position">
                    <div class="info-label">Албан тушаал</div>
                    <div class="info-value">{{ submission.position }}</div>
                  </div>
                  
                  <div class="info-item full-width" v-if="submission.eventName">
                    <div class="info-label">Сонгосон арга хэмжээ</div>
                    <div class="info-value">
                      <strong>{{ submission.eventName }}</strong>
                      <br>
                      <small>{{ submission.eventDate }} | {{ submission.eventLocation }}</small>
                    </div>
                  </div>
                  
                  <div class="info-item full-width" v-if="submission.interests && submission.interests.length">
                    <div class="info-label">Сонирхож буй чиглэл</div>
                    <div class="info-value">
                      <span class="tag" v-for="interest in submission.interests" :key="interest">{{ interest }}</span>
                    </div>
                  </div>
                  
                  <div class="info-item full-width" v-if="submission.visas && submission.visas.length">
                    <div class="info-label">Визний мэдээлэл</div>
                    <div class="info-value">
                      <span class="tag" v-for="visa in submission.visas" :key="visa">{{ visa }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card-actions">
                <button @click="exportSubmission(submission)" class="action-btn">
                  <span>📥</span> Export JSON
                </button>
              </div>
            </div>
          </div>

          <div class="export-section">
            <button @click="exportAll" class="export-all-btn">
              📊 Бүгдийг татах (CSV)
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from '../firebase'
import { collection, getDocs, query, orderBy } from 'firebase/firestore'

export default {
  name: 'FormSubmissionsPage',
  data() {
    return {
      submissions: [],
      loading: true
    }
  },
  async mounted() {
    await this.loadSubmissions()
  },
  methods: {
    async loadSubmissions() {
      try {
        this.loading = true
        const q = query(collection(db, 'eventRegistrations'), orderBy('createdAt', 'desc'))
        const querySnapshot = await getDocs(q)
        
        this.submissions = []
        querySnapshot.forEach((doc) => {
          this.submissions.push({
            id: doc.id,
            ...doc.data()
          })
        })
      } catch (error) {
        console.error('Error loading submissions:', error)
        alert('Мэдээлэл уншихад алдаа гарлаа')
      } finally {
        this.loading = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleString('mn-MN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    exportSubmission(submission) {
      const data = JSON.stringify(submission, null, 2)
      const blob = new Blob([data], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `submission-${submission.id}.json`
      a.click()
      URL.revokeObjectURL(url)
    },
    exportAll() {
      const headers = ['Овог нэр', 'Байгууллага', 'Имэйл', 'Утас', 'Албан тушаал', 'Арга хэмжээ', 'Огноо', 'Байршил', 'Сонирхол', 'Виз', 'Бүртгэл']
      const rows = this.submissions.map(s => [
        s.fullName || '',
        s.organization || '',
        s.email || '',
        s.phone || '',
        s.position || '',
        s.eventName || '',
        s.eventDate || '',
        s.eventLocation || '',
        (s.interests || []).join('; '),
        (s.visas || []).join('; '),
        this.formatDate(s.createdAt)
      ])
      
      const csv = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n')
      
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `event-registrations-${new Date().toISOString().split('T')[0]}.csv`
      a.click()
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.submissions-page {
  min-height: 100vh;
  background: #f0ebf8;
  padding: 100px 12px 60px;
}

.page-container {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 15px;
}

.header-card {
  background: white;
  border-radius: 8px;
  padding: 32px;
  margin-bottom: 20px;
  border-top: 10px solid #673ab7;
}

.page-title {
  font-size: 32px;
  font-weight: 400;
  color: #202124;
  margin: 0 0 24px 0;
  font-family: 'Google Sans', Roboto, Arial, sans-serif;
}

.stats-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.stat-card {
  background: #f8f9fa;
  padding: 20px 28px;
  border-radius: 8px;
  border-left: 4px solid #673ab7;
}

.stat-number {
  font-size: 36px;
  font-weight: 500;
  color: #673ab7;
  margin-bottom: 4px;
  font-family: 'Google Sans', Roboto, Arial, sans-serif;
}

.stat-label {
  font-size: 13px;
  color: #5f6368;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.content-area {
  margin-top: 20px;
}

.loading-state {
  background: white;
  padding: 80px;
  border-radius: 8px;
  text-align: center;
  border: 1px solid #dadce0;
}

.loader {
  width: 48px;
  height: 48px;
  border: 4px solid #f0ebf8;
  border-top: 4px solid #673ab7;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-state p {
  color: #5f6368;
  font-size: 14px;
  margin: 0;
}

.empty-state {
  background: white;
  padding: 80px 40px;
  border-radius: 8px;
  text-align: center;
  border: 1px solid #dadce0;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-state h2 {
  font-size: 20px;
  font-weight: 400;
  color: #202124;
  margin: 0 0 8px 0;
  font-family: 'Google Sans', Roboto, Arial, sans-serif;
}

.empty-state p {
  font-size: 14px;
  color: #5f6368;
  margin: 0;
}

.submissions-grid {
  display: grid;
  gap: 16px;
}

.submission-card {
  background: white;
  border-radius: 8px;
  border: 1px solid #dadce0;
  overflow: hidden;
  transition: all 0.2s;
}

.submission-card:hover {
  border-color: #673ab7;
  box-shadow: 0 1px 3px 0 rgba(60,64,67,0.3), 0 4px 8px 3px rgba(60,64,67,0.15);
}

.card-header {
  background: #673ab7;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.submission-number {
  font-size: 16px;
  font-weight: 500;
  color: white;
  font-family: 'Google Sans', Roboto, Arial, sans-serif;
}

.submission-date {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
}

.card-content {
  padding: 24px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-label {
  font-size: 12px;
  color: #5f6368;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 14px;
  color: #202124;
  line-height: 1.5;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  display: inline-block;
  padding: 4px 12px;
  background: #f0ebf8;
  color: #673ab7;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
}

.card-actions {
  padding: 16px 24px;
  border-top: 1px solid #dadce0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.action-btn {
  padding: 8px 16px;
  background: white;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-size: 13px;
  color: #5f6368;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Google Sans', Roboto, Arial, sans-serif;
}

.action-btn:hover {
  background: #f8f9fa;
  border-color: #673ab7;
  color: #673ab7;
}

.export-section {
  margin-top: 24px;
  text-align: center;
  padding: 24px;
  background: white;
  border-radius: 8px;
  border: 1px solid #dadce0;
}

.export-all-btn {
  padding: 12px 32px;
  background: #673ab7;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: 'Google Sans', Roboto, Arial, sans-serif;
}

.export-all-btn:hover {
  background: #7c4dff;
  box-shadow: 0 1px 3px 0 rgba(60,64,67,0.3), 0 4px 8px 3px rgba(60,64,67,0.15);
}

@media (max-width: 768px) {
  .submissions-page {
    padding: 85px 8px 40px;
  }
  
  .page-container {
    padding: 0 10px;
  }

  .header-card {
    padding: 20px 16px;
  }

  .page-title {
    font-size: 24px;
    line-height: 1.3;
  }
  
  .stat-number {
    font-size: 32px;
  }
  
  .stat-label {
    font-size: 12px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 14px 18px;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .card-content {
    padding: 18px 16px;
  }

  .card-actions {
    padding: 12px 16px;
  }
  
  .action-btn {
    font-size: 12px;
    padding: 7px 14px;
  }
  
  .export-section {
    padding: 20px 16px;
  }
  
  .export-all-btn {
    padding: 11px 28px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .submissions-page {
    padding: 80px 5px 35px;
  }
  
  .page-title {
    font-size: 22px;
  }
  
  .stat-number {
    font-size: 28px;
  }
  
  .card-header {
    padding: 12px 14px;
  }
  
  .submission-number {
    font-size: 15px;
  }
  
  .submission-date {
    font-size: 12px;
  }
  
  .card-content {
    padding: 16px 14px;
  }
  
  .info-label {
    font-size: 11px;
  }
  
  .info-value {
    font-size: 13px;
  }
  
  .tag {
    font-size: 12px;
    padding: 3px 10px;
  }
}
</style>
